/**
 * TrackDiscussion Component - Step 8: Persistent song discussion threads
 * Shows comments on a track with reply support
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Heart, Send, Reply, X, ChevronDown, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Comment = ({ comment, onLike, onReply, currentUserId }) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  
  const isLiked = comment.likes?.includes(currentUserId);
  
  const handleSubmitReply = () => {
    if (replyText.trim()) {
      onReply(comment.id, replyText.trim());
      setReplyText('');
      setShowReplyInput(false);
    }
  };

  return (
    <div className="py-3">
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center flex-shrink-0 overflow-hidden">
          {comment.user_avatar ? (
            <img src={comment.user_avatar} alt={comment.user_name} className="w-full h-full object-cover" />
          ) : (
            <User className="w-4 h-4 text-white" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-white font-medium text-sm">{comment.user_name}</span>
            <span className="text-[#52525B] text-xs">
              {new Date(comment.created_at).toLocaleDateString()}
            </span>
          </div>
          
          <p className="text-[#A1A1AA] text-sm leading-relaxed">{comment.text}</p>
          
          {/* Actions */}
          <div className="flex items-center gap-4 mt-2">
            <button
              onClick={() => onLike(comment.id)}
              className={`flex items-center gap-1 text-xs transition-colors ${
                isLiked ? 'text-[#FF007F]' : 'text-[#52525B] hover:text-white'
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-current' : ''}`} />
              {comment.like_count || 0}
            </button>
            
            <button
              onClick={() => setShowReplyInput(!showReplyInput)}
              className="flex items-center gap-1 text-xs text-[#52525B] hover:text-white transition-colors"
            >
              <Reply className="w-3.5 h-3.5" />
              Reply
            </button>
          </div>
          
          {/* Reply Input */}
          <AnimatePresence>
            {showReplyInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3"
              >
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={`Reply to ${comment.user_name}...`}
                    className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmitReply()}
                  />
                  <button
                    onClick={handleSubmitReply}
                    disabled={!replyText.trim()}
                    className="px-3 py-2 rounded-lg bg-[#00F0FF] text-[#050505] disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const TrackDiscussion = ({ trackId, trackTitle, isOpen, onClose, noTrack }) => {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [posting, setPosting] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!trackId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${API_URL}/api/artists/radio/tracks/${trackId}/comments`
      );
      
      if (response.data) {
        setComments(response.data.comments || []);
        setTotal(response.data.total || 0);
        setHasMore(response.data.has_more || false);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [trackId]);

  useEffect(() => {
    if (isOpen && trackId) {
      fetchComments();
    }
  }, [isOpen, trackId, fetchComments]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      return;
    }
    
    if (!isAuthenticated) {
      setError('Please sign in to comment');
      return;
    }

    if (!trackId) {
      setError('No track selected');
      return;
    }
    
    try {
      setPosting(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/artists/radio/tracks/${trackId}/comments`,
        { text: newComment.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setComments([response.data.comment, ...comments]);
        setTotal(total + 1);
        setNewComment('');
      } else {
        setError('Failed to post comment');
      }
    } catch (err) {
      console.error('Error posting comment:', err);
      setError(err.response?.data?.detail || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!isAuthenticated) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/artists/radio/tracks/${trackId}/comments/${commentId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setComments(comments.map(c => {
        if (c.id === commentId) {
          const isLiked = c.likes?.includes(user.id);
          return {
            ...c,
            likes: isLiked 
              ? c.likes.filter(id => id !== user.id)
              : [...(c.likes || []), user.id],
            like_count: isLiked ? (c.like_count || 1) - 1 : (c.like_count || 0) + 1
          };
        }
        return c;
      }));
    } catch (err) {
      console.error('Error liking comment:', err);
    }
  };

  const handleReply = async (parentId, text) => {
    if (!isAuthenticated) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/artists/radio/tracks/${trackId}/comments`,
        { text, reply_to: parentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setComments([response.data.comment, ...comments]);
        setTotal(total + 1);
      }
    } catch (err) {
      console.error('Error posting reply:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end justify-center"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Panel */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        className="relative w-full max-w-lg bg-[#12101A] rounded-t-3xl flex flex-col"
        style={{ maxHeight: 'calc(80vh - 150px)', marginBottom: '150px' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>
        
        {/* Header */}
        <div className="px-4 pb-3 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#00F0FF]" />
              Discussion
            </h3>
            <p className="text-[#71717A] text-sm truncate">{trackTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-[#71717A] hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Comments List */}
        <div className="flex-1 overflow-y-auto px-4">
          {loading ? (
            <div className="py-8 text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-6 h-6 mx-auto border-2 border-[#00F0FF] border-t-transparent rounded-full"
              />
            </div>
          ) : comments.length === 0 ? (
            <div className="py-12 text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-[#52525B]" />
              <p className="text-[#71717A]">No comments yet</p>
              <p className="text-[#52525B] text-sm">Be the first to start the discussion</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {comments.map((comment) => (
                <Comment
                  key={comment.id}
                  comment={comment}
                  onLike={handleLikeComment}
                  onReply={handleReply}
                  currentUserId={user?.id}
                />
              ))}
            </div>
          )}
          
          {hasMore && (
            <button className="w-full py-3 text-[#00F0FF] text-sm hover:underline flex items-center justify-center gap-1">
              <ChevronDown className="w-4 h-4" />
              Load more comments
            </button>
          )}
        </div>
        
        {/* Comment Input */}
        <div className="p-4 border-t border-white/10">
          {error && (
            <p className="text-red-400 text-sm mb-2 text-center">{error}</p>
          )}
          {noTrack ? (
            <p className="text-center text-[#71717A] py-2">
              Play a track to start the discussion
            </p>
          ) : isAuthenticated ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => {
                  setNewComment(e.target.value);
                  setError(null);
                }}
                placeholder="Add a comment..."
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
                onKeyDown={(e) => e.key === 'Enter' && !posting && handleSubmitComment()}
                disabled={posting}
                data-testid="comment-input"
              />
              <button
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || posting}
                className="px-4 py-3 rounded-xl bg-[#00F0FF] text-[#050505] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[52px]"
                data-testid="submit-comment-btn"
              >
                {posting ? (
                  <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          ) : (
            <p className="text-center text-[#71717A] py-2">
              <a href="/auth" className="text-[#00F0FF] hover:underline">Sign in</a> to join the discussion
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TrackDiscussion;
